import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import DOMPurify from 'dompurify';
import { API } from '../../utils/api';
import { useSubscriptionFeatures } from '../../utils/useSubscriptionFeatures';
import { useSubscription } from '../../context/SubscriptionContext';
import SubscriptionFeatureNotice from '../SubscriptionFeatureNotice';
import WelcomeIntroModal from '../WelcomeIntroModal';
import AddSiteModal from '../AddSiteModal';
import AddTaskModal from '../AddTaskModal';

// Drag and Drop imports
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  rectSortingStrategy,
} from '@dnd-kit/sortable';
import {
  useSortable,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

// Individual Widget Components
const SortableWidget = ({ id, children }) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.8 : 1,
    zIndex: isDragging ? 1000 : 1,
  };

  return (
    <div ref={setNodeRef} style={style} className="relative group overflow-visible">
      <div 
        {...attributes} 
        {...listeners}
        className="absolute top-3 right-3 z-20 cursor-grab active:cursor-grabbing p-2 rounded-lg bg-slate-700 border border-slate-600 shadow-sm hover:shadow-md transition-all duration-200 opacity-0 group-hover:opacity-100"
        title="Drag to reorder widget"
      >
        <svg className="w-4 h-4 text-gray-400" fill="currentColor" viewBox="0 0 20 20">
          <path d="M7 2a1 1 0 011 1v2h4V3a1 1 0 112 0v2h2a2 2 0 012 2v2H3V7a2 2 0 012-2h2V3a1 1 0 011-1zM3 11v6a2 2 0 002 2h10a2 2 0 002-2v-6H3z" />
        </svg>
      </div>
      {children}
    </div>
  );
};

const MetricsCardsWidget = ({ users, sites, photosCount, tasksCount, loading }) => {
  // Helper function for pluralization
  const pluralize = (count, singular, plural) => count === 1 ? singular : plural;
  
  const activeTeamCount = users.filter(user => user.active !== false).length;
  const activeSitesCount = sites.filter(site => site.status === 'active').length;
  
  return (
    <div className="bg-slate-800 rounded-2xl shadow-lg border border-slate-700 p-6 h-full">
      <h3 className="text-lg font-semibold text-white mb-4 flex items-center">
        <svg className="w-5 h-5 mr-2 text-purple-400" fill="currentColor" viewBox="0 0 20 20">
          <path d="M2 11a1 1 0 011-1h2a1 1 0 011 1v5a1 1 0 01-1 1H3a1 1 0 01-1-1v-5zM8 7a1 1 0 011-1h2a1 1 0 011 1v9a1 1 0 01-1 1H9a1 1 0 01-1-1V7zM14 4a1 1 0 011-1h2a1 1 0 011 1v12a1 1 0 01-1 1h-2a1 1 0 01-1-1V4z" />
        </svg>
        📊 Key Metrics
      </h3>
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <div className="text-center p-4 bg-yellow-500/20 rounded-lg border border-yellow-500/30">
          <div className="text-2xl font-bold text-white">{activeTeamCount}</div>
          <div className="text-sm text-gray-300">{pluralize(activeTeamCount, 'Active Team Member', 'Active Team Members')}</div>
        </div>
        <div className="text-center p-4 bg-amber-500/20 rounded-lg border border-amber-500/30">
          <div className="text-2xl font-bold text-white">{activeSitesCount}</div>
          <div className="text-sm text-gray-300">{pluralize(activeSitesCount, 'Active Site', 'Active Sites')}</div>
        </div>
        <div className="text-center p-4 bg-orange-500/20 rounded-lg border border-orange-500/30">
          <div className="text-2xl font-bold text-white">{photosCount}</div>
          <div className="text-sm text-gray-300">{pluralize(photosCount, 'Photo This Week', 'Photos This Week')}</div>
        </div>
        <div className="text-center p-4 bg-green-500/20 rounded-lg border border-green-500/30">
          <div className="text-2xl font-bold text-white">{tasksCount}</div>
          <div className="text-sm text-gray-300">Active Tasks This Week</div>
        </div>
      </div>
    </div>
  );
};

const TodaysAttendanceWidget = ({ attendanceData, loading }) => (
  <div className="bg-slate-800 rounded-2xl shadow-lg border border-slate-700 p-6 h-full overflow-visible" style={{ zIndex: 1 }}>
    <h3 className="text-lg font-semibold text-white mb-4 flex items-center">
      <svg className="w-5 h-5 mr-2 text-green-400" fill="currentColor" viewBox="0 0 20 20">
        <path fillRule="evenodd" d="M10 9a3 3 0 100-6 3 3 0 000 6zm-7 9a7 7 0 1114 0H3z" clipRule="evenodd" />
      </svg>
      Clock-In Status
    </h3>
    {loading ? (
      <div className="flex items-center justify-center py-8">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-500"></div>
      </div>
    ) : attendanceData.length === 0 ? (
      <div className="text-center py-8 text-gray-400">
        <div className="text-4xl mb-2">🏗️</div>
        <div className="text-sm">No active sites</div>
        <div className="text-xs">Activate sites to see attendance data</div>
      </div>
    ) : (
      <div className="relative overflow-visible" style={{ zIndex: 2 }}>
        <div className="py-2">
          <div className="flex flex-wrap gap-4 justify-start">
            {attendanceData.map((siteData, index) => (
              <div key={siteData.site_id} className="flex-shrink-0 w-56 bg-slate-700 rounded-lg p-2 border border-slate-600 relative overflow-visible">
                {/* Site Header */}
                <div className="mb-2">
                  <h4 className="font-medium text-white text-sm truncate" title={siteData.site_name}>
                    {siteData.site_name}
                  </h4>
                </div>
                
                {/* Attendance Metrics - Only Clocked In and Late */}
                <div className="grid grid-cols-2 gap-3 overflow-visible">
                  {/* Clocked In */}
                  <div className="relative text-center p-3 bg-green-500/20 rounded border border-green-500/30 cursor-help group/clockedin overflow-visible">
                    <div className="text-xl font-bold text-green-400">{siteData.clockedIn}</div>
                    <div className="text-xs text-green-300">Clocked In</div>
                    
                    {/* Hover tooltip for clocked in workers */}
                    <div 
                      className="absolute top-full left-1/2 transform -translate-x-1/2 mt-2 px-3 py-2 bg-slate-900 text-white text-xs rounded-lg opacity-0 group-hover/clockedin:opacity-100 transition-opacity duration-300 pointer-events-none min-w-max max-w-xs shadow-xl border border-slate-600"
                      style={{ 
                        zIndex: 99999,
                        position: 'absolute',
                        whiteSpace: 'nowrap'
                      }}
                    >
                      <div className="font-medium mb-1">Clocked-In:</div>
                      {siteData.clockedInWorkers.length > 0 ? (
                        <div className="space-y-1">
                          {siteData.clockedInWorkers.slice(0, 5).map((worker, idx) => {
                            const formatName = (fullName) => {
                              const nameParts = fullName.split(' ').filter(part => part.length > 0);
                              if (nameParts.length === 1) return nameParts[0];
                              const firstName = nameParts[0];
                              const initials = nameParts.slice(1).map(name => name.charAt(0).toUpperCase() + '.').join(' ');
                              return `${firstName} ${initials}`;
                            };
                            return (
                              <div key={idx} className="text-xs">
                                <span className="truncate">{formatName(worker.name)}</span>
                              </div>
                            );
                          })}
                          {siteData.clockedInWorkers.length > 5 && (
                            <div className="text-xs text-gray-400 mt-1">+{siteData.clockedInWorkers.length - 5} more</div>
                          )}
                        </div>
                      ) : (
                        <div className="text-xs text-gray-400">-</div>
                      )}
                      {/* Tooltip arrow */}
                      <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 border-4 border-transparent border-b-slate-900"></div>
                    </div>
                  </div>
                  
                  {/* Late */}
                  <div className="relative text-center p-3 bg-yellow-500/20 rounded border border-yellow-500/30 cursor-help group/late overflow-visible">
                    <div className="text-xl font-bold text-yellow-400">{siteData.late}</div>
                    <div className="text-xs text-yellow-300">Late</div>
                    
                    {/* Hover tooltip for late workers */}
                    <div 
                      className="absolute top-full left-1/2 transform -translate-x-1/2 mt-2 px-3 py-2 bg-slate-900 text-white text-xs rounded-lg opacity-0 group-hover/late:opacity-100 transition-opacity duration-300 pointer-events-none min-w-max max-w-xs shadow-xl border border-slate-600"
                      style={{ 
                        zIndex: 99999,
                        position: 'absolute',
                        whiteSpace: 'nowrap'
                      }}
                    >
                      <div className="font-medium mb-1">Late:</div>
                      {siteData.lateWorkers.length > 0 ? (
                        <div className="space-y-1">
                          {siteData.lateWorkers.slice(0, 5).map((worker, idx) => {
                            const formatName = (fullName) => {
                              const nameParts = fullName.split(' ').filter(part => part.length > 0);
                              if (nameParts.length === 1) return nameParts[0];
                              const firstName = nameParts[0];
                              const initials = nameParts.slice(1).map(name => name.charAt(0).toUpperCase() + '.').join(' ');
                              return `${firstName} ${initials}`;
                            };
                            return (
                              <div key={idx} className="text-xs flex justify-between items-center gap-3">
                                <span className="truncate">{formatName(worker.name)}</span>
                                <span className="text-yellow-300 flex-shrink-0">{worker.clockInTime}</span>
                              </div>
                            );
                          })}
                          {siteData.lateWorkers.length > 5 && (
                            <div className="text-xs text-gray-400 mt-1">+{siteData.lateWorkers.length - 5} more</div>
                          )}
                        </div>
                      ) : (
                        <div className="text-xs text-gray-400">-</div>
                      )}
                      {/* Tooltip arrow */}
                      <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 border-4 border-transparent border-b-slate-900"></div>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    )}
  </div>
);

const TaskCompletionWidget = ({ completionData, loading }) => (
  <div className="bg-slate-800 rounded-2xl shadow-lg border border-slate-700 p-6 h-full">
    <h3 className="text-lg font-semibold text-white mb-4 flex items-center">
      <svg className="w-5 h-5 mr-2 text-green-400" fill="currentColor" viewBox="0 0 20 20">
        <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
      </svg>
      Tasks
    </h3>
    {loading ? (
      <div className="flex items-center justify-center py-8">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-500"></div>
      </div>
    ) : completionData.length === 0 ? (
      <div className="text-center py-8 text-gray-400">
        <div className="text-4xl mb-2">📋</div>
        <div className="text-sm">No active sites</div>
        <div className="text-xs">Activate sites to see task data</div>
      </div>
    ) : (
      <div className="relative overflow-visible">
        <div className="overflow-x-auto overflow-y-visible py-2">
          <div className="flex space-x-4 min-w-full px-2" style={{ minWidth: `${completionData.length * 200}px` }}>
            {completionData.map((siteData, index) => (
              <div key={siteData.site_id} className="flex-shrink-0 w-48 bg-slate-700 rounded-lg p-3 border border-slate-600">
                {/* Site Header */}
                <div className="mb-3">
                  <h4 className="font-medium text-white text-sm truncate" title={siteData.site_name}>
                    {siteData.site_name}
                  </h4>
                </div>
                
                {/* Task Metrics - Total, Completed, Pending */}
                <div className="space-y-3">
                  {/* Total */}
                  <div className="text-center p-3 bg-blue-500/20 rounded border border-blue-500/30">
                    <div className="text-xl font-bold text-blue-400">{siteData.total}</div>
                    <div className="text-xs text-blue-300">Total</div>
                  </div>
                  
                  {/* Completed */}
                  <div className="text-center p-3 bg-green-500/20 rounded border border-green-500/30">
                    <div className="text-xl font-bold text-green-400">{siteData.completed}</div>
                    <div className="text-xs text-green-300">Completed</div>
                  </div>
                  
                  {/* Pending */}
                  <div className="text-center p-3 bg-yellow-500/20 rounded border border-yellow-500/30">
                    <div className="text-xl font-bold text-yellow-400">{siteData.pending}</div>
                    <div className="text-xs text-yellow-300">Pending</div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    )}
  </div>
);

const WeatherImpactWidget = ({ weatherData, loading }) => (
  <div className="bg-slate-800 rounded-2xl shadow-lg border border-slate-700 p-6 h-full">
    <h3 className="text-lg font-semibold text-white mb-4 flex items-center">
      <svg className="w-5 h-5 mr-2 text-cyan-400" fill="currentColor" viewBox="0 0 20 20">
        <path fillRule="evenodd" d="M5.05 4.05a7 7 0 119.9 9.9L10 18.9l-4.95-4.95a7 7 0 010-9.9zM10 11a2 2 0 100-4 2 2 0 000 4z" clipRule="evenodd" />
      </svg>
      Weather Info
    </h3>
    
    {loading ? (
      <div className="flex items-center justify-center py-8">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-500"></div>
      </div>
    ) : weatherData.length === 0 ? (
      <div className="text-center py-8 text-gray-400">
        <div className="text-4xl mb-2">🏗️</div>
        <div className="text-sm">No active sites</div>
        <div className="text-xs">Activate sites to see weather data</div>
      </div>
    ) : (
      <div className="relative overflow-visible">
        <div className="overflow-x-auto overflow-y-visible py-2">
          <div className="flex space-x-4 min-w-full px-2" style={{ minWidth: `${weatherData.length * 280}px` }}>
            {weatherData.map((siteWeather, index) => (
              <div key={siteWeather.site_id} className="flex-shrink-0 w-64 bg-slate-700 rounded-lg p-3 border border-slate-600">
                {/* Site Header */}
                <div className="mb-3">
                  <h4 className="font-medium text-white text-sm truncate" title={siteWeather.site_name}>
                    {siteWeather.site_name}
                  </h4>
                  {siteWeather.hasAddress && (
                    <div className="text-xs text-gray-400 truncate" title={siteWeather.location}>
                      {siteWeather.location}
                    </div>
                  )}
                </div>
                
                {/* Weather Info or Address Prompt */}
                {siteWeather.hasAddress ? (
                  <div className="space-y-3">
                    {/* Temperature and Condition */}
                    <div className="flex items-center justify-between p-3 bg-cyan-500/20 rounded border border-cyan-500/30">
                      <div>
                        <div className="text-2xl font-bold text-white">{siteWeather.temperature}°C</div>
                        <div className="text-sm text-gray-300">{siteWeather.condition}</div>
                      </div>
                      <div className="text-3xl">{siteWeather.icon}</div>
                    </div>
                    
                    {/* Weather Alerts */}
                    <div>
                      {siteWeather.alerts && siteWeather.alerts.length > 0 ? (
                        siteWeather.alerts.slice(0, 2).map((alert, alertIndex) => (
                          <div key={alertIndex} className="p-2 bg-yellow-500/20 rounded border border-yellow-500/30 mb-2">
                            <div className="text-xs font-medium text-yellow-300">{alert.type}</div>
                            <div className="text-xs text-yellow-200 truncate" title={alert.message}>
                              {alert.message}
                            </div>
                          </div>
                        ))
                      ) : (
                        <div className="p-2 bg-green-500/20 rounded border border-green-500/30 text-center">
                          <div className="text-xs text-green-300">No weather alerts</div>
                          <div className="text-xs text-green-400">Good conditions</div>
                        </div>
                      )}
                    </div>
                    
                    {/* Last Updated */}
                    <div className="text-xs text-gray-400 text-center">
                      Updated: {siteWeather.lastUpdated}
                    </div>
                  </div>
                ) : (
                  /* Address Required Prompt */
                  <div className="space-y-3">
                    <div className="p-4 bg-yellow-500/20 rounded-lg border border-yellow-500/30">
                      <div className="text-yellow-400 mb-2 text-center">
                        <div className="text-3xl mb-2">{siteWeather.icon}</div>
                      </div>
                      <div className="text-sm text-yellow-300 font-medium text-center">No Address Information</div>
                      <div className="text-xs text-yellow-400 mt-2 text-center">
                        Please set the address for this site to get weather data
                      </div>
                      <button 
                        onClick={() => {
                          // Store the selected site ID and navigate to site management
                          localStorage.setItem('editSiteId', siteWeather.site_id);
                          window.location.href = '/admin/sites';
                        }}
                        className="mt-3 w-full bg-yellow-600 text-white text-xs py-2 px-3 rounded-md hover:bg-yellow-500 transition-colors"
                      >
                        Update Site Address
                      </button>
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </div>
    )}
  </div>
);

const RegulatoryComplianceWidget = ({ complianceData, loading }) => (
  <div className="bg-slate-800 rounded-2xl shadow-lg border border-slate-700 p-6">
    <h3 className="text-lg font-semibold text-white mb-4 flex items-center">
      <svg className="w-5 h-5 mr-2 text-purple-400" fill="currentColor" viewBox="0 0 20 20">
        <path fillRule="evenodd" d="M4 4a2 2 0 012-2h8a2 2 0 012 2v12a1 1 0 110 2h-3a1 1 0 01-1-1v-6a1 1 0 00-1-1H9a1 1 0 00-1 1v6a1 1 0 01-1 1H4a1 1 0 110-2V4z" clipRule="evenodd" />
      </svg>
      Regulatory Compliance
    </h3>
    {loading ? (
      <div className="flex items-center justify-center py-8">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-500"></div>
      </div>
    ) : (
      <div className="space-y-4">
        <div className="grid grid-cols-3 gap-3 text-center">
          <div className="p-3 bg-green-500/20 rounded-lg border border-green-500/30">
            <div className="text-lg font-bold text-green-400">{complianceData.upToDate}</div>
            <div className="text-xs text-green-300">Up to Date</div>
          </div>
          <div className="p-3 bg-yellow-500/20 rounded-lg border border-yellow-500/30">
            <div className="text-lg font-bold text-yellow-400">{complianceData.dueSoon}</div>
            <div className="text-xs text-yellow-300">Due Soon</div>
          </div>
          <div className="p-3 bg-red-500/20 rounded-lg border border-red-500/30">
            <div className="text-lg font-bold text-red-400">{complianceData.overdue}</div>
            <div className="text-xs text-red-300">Overdue</div>
          </div>
        </div>
        
        {complianceData.upcomingDeadlines.length > 0 && (
          <div>
            <h4 className="text-sm font-medium text-gray-300 mb-2">Upcoming Deadlines</h4>
            <div className="space-y-2">
              {complianceData.upcomingDeadlines.slice(0, 3).map((item, index) => (
                <div key={index} className="flex justify-between items-center p-2 bg-slate-700 rounded">
                  <span className="text-xs text-gray-300 truncate">{item.name}</span>
                  <span className={`text-xs px-2 py-1 rounded-full ${
                    item.urgency === 'high' ? 'bg-red-500/20 text-red-400' :
                    item.urgency === 'medium' ? 'bg-yellow-500/20 text-yellow-400' :
                    'bg-green-500/20 text-green-400'
                  }`}>
                    {item.dueDate}
                  </span>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    )}
  </div>
);

const RecentActivityWidget = ({ recentActivity, loading }) => {
  const formatTime = (timestamp) => {
    const date = new Date(timestamp);
    return date.toLocaleTimeString('en-GB', { 
      hour: '2-digit', 
      minute: '2-digit',
      hour12: false 
    });
  };

  const formatDate = (timestamp) => {
    const date = new Date(timestamp);
    return date.toLocaleDateString('en-GB', { 
      weekday: 'short',
      month: 'short', 
      day: 'numeric' 
    });
  };

  const getTimeAgo = (timestamp) => {
    const now = new Date();
    const activityTime = new Date(timestamp);
    const diffMs = now - activityTime;
    const diffMinutes = Math.floor(diffMs / (1000 * 60));
    
    if (diffMinutes < 1) return 'Just now';
    if (diffMinutes < 60) return `${diffMinutes} min ago`;
    
    const diffHours = Math.floor(diffMinutes / 60);
    if (diffHours < 24) return `${diffHours} hour${diffHours > 1 ? 's' : ''} ago`;
    
    const diffDays = Math.floor(diffHours / 24);
    return `${diffDays} day${diffDays > 1 ? 's' : ''} ago`;
  };

  const highlightDynamicData = (description) => {
    // Sanitize input first to prevent XSS
    let highlightedText = DOMPurify.sanitize(description, { ALLOWED_TAGS: [] });
    
    // Highlight user names (names with 2+ words, first letter capitalized)
    highlightedText = highlightedText.replace(
      /\b[A-Z][a-z]+(?:\s+[A-Z][a-z]+)+\b/g,
      '<span class="font-semibold">$&</span>'
    );
    
    // Highlight site names (pattern: "at [Site Name]" or references to sites)
    highlightedText = highlightedText.replace(
      /\bat\s+([^,\.]+?)(?=\s|$)/g,
      'at <span class="font-semibold">$1</span>'
    );
    
    // Highlight task titles in quotes
    highlightedText = highlightedText.replace(
      /'([^']+)'/g,
      "'<span class=\"font-semibold\">$1</span>'"
    );
    
    // Highlight status words (completed, in_progress, pending, active, inactive, uploaded, updated, created)
    highlightedText = highlightedText.replace(
      /\b(completed|in_progress|pending|active|inactive|uploaded|updated|created)\b/gi,
      '<span class="font-semibold">$&</span>'
    );
    
    return highlightedText;
  };

  return (
    <div className="bg-slate-800 rounded-2xl shadow-lg border border-slate-700 p-6 h-full flex flex-col">
      <h3 className="text-lg font-semibold text-white mb-4 flex items-center flex-shrink-0">
        <svg className="w-5 h-5 mr-2 text-indigo-400" fill="currentColor" viewBox="0 0 20 20">
          <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-12a1 1 0 10-2 0v4a1 1 0 00.293.707l2.828 2.829a1 1 0 101.415-1.415L11 9.586V6z" clipRule="evenodd" />
        </svg>
        Recent Activity
      </h3>
      {loading ? (
        <div className="flex items-center justify-center py-8">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-500"></div>
        </div>
      ) : recentActivity.length === 0 ? (
        <div className="text-center py-8 text-gray-400">
          <div className="text-4xl mb-2">🌟</div>
          <div className="text-sm">No recent activity</div>
          <div className="text-xs">Activity will appear here as your team starts working</div>
        </div>
      ) : (
        <div className="flex-1 overflow-hidden">
          <div className="max-h-48 overflow-y-auto overflow-x-hidden">
            <div className="space-y-3 pr-2">
              {recentActivity.map((activity, index) => (
              <div key={index} className="flex items-start py-2 hover:bg-slate-700 rounded-lg px-3 -mx-3 transition-colors duration-200">
                <div className="flex-shrink-0 text-lg mr-3 mt-0.5">
                  {activity.icon}
                </div>
                <div className="flex-1 min-w-0 overflow-hidden">
                  <p className="text-sm font-medium text-white truncate" title={activity.title}>
                    {activity.title}
                  </p>
                  <p 
                    className="text-xs text-gray-400 leading-relaxed break-words"
                    dangerouslySetInnerHTML={{ __html: highlightDynamicData(activity.description) }}
                  />
                  <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center mt-1 gap-1">
                    <p className="text-xs text-gray-500 truncate">
                      {formatDate(activity.timestamp)} at {formatTime(activity.timestamp)}
                    </p>
                    <span className="text-xs text-gray-400 font-medium flex-shrink-0">
                      {getTimeAgo(activity.timestamp)}
                    </span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
        </div>
      )}
    </div>
  );
};

const QuickActionsWidget = ({ 
  navigate,
  setShowAddSiteModal, 
  setShowAddTaskModal,
  onNavigateToReports 
}) => (
  <div className="bg-slate-800 rounded-2xl shadow-lg border border-slate-700 p-6 h-full">
    <h3 className="text-lg font-semibold text-white mb-4 flex items-center">
      <svg className="w-5 h-5 mr-2 text-yellow-400" fill="currentColor" viewBox="0 0 20 20">
        <path fillRule="evenodd" d="M11.3 1.046A1 1 0 0112 2v5h4a1 1 0 01.82 1.573l-7 10A1 1 0 018 18v-5H4a1 1 0 01-.82-1.573l7-10a1 1 0 011.12-.38z" clipRule="evenodd" />
      </svg>
      Quick Actions
    </h3>
    <div className="grid grid-cols-2 gap-3">
      <button 
        onClick={() => navigate('/admin/users?openAddModal=true')}
        className="p-3 text-left rounded-lg bg-slate-700/50 hover:bg-yellow-500/20 transition-colors duration-200 border border-slate-600 hover:border-yellow-500/50 group"
      >
        <div className="flex items-center">
          <div className="w-8 h-8 bg-yellow-500/20 rounded-lg flex items-center justify-center mr-3 group-hover:bg-yellow-500/30 transition-colors duration-200 border border-yellow-500/30">
            <svg className="w-4 h-4 text-yellow-400" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M10 9a3 3 0 100-6 3 3 0 000 6zm-7 9a7 7 0 1114 0H3z" clipRule="evenodd" />
            </svg>
          </div>
          <div>
            <p className="font-medium text-white text-sm">Add Team Member</p>
          </div>
        </div>
      </button>
      
      <button 
        onClick={() => setShowAddSiteModal(true)}
        className="p-3 text-left rounded-lg bg-slate-700/50 hover:bg-amber-500/20 transition-colors duration-200 border border-slate-600 hover:border-amber-500/50 group"
      >
        <div className="flex items-center">
          <div className="w-8 h-8 bg-amber-500/20 rounded-lg flex items-center justify-center mr-3 group-hover:bg-amber-500/30 transition-colors duration-200 border border-amber-500/30">
            <svg className="w-4 h-4 text-amber-400" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M4 4a2 2 0 012-2h8a2 2 0 012 2v12a1 1 0 110 2h-3a1 1 0 01-1-1v-6a1 1 0 00-1-1H9a1 1 0 00-1 1v6a1 1 0 01-1 1H4a1 1 0 110-2V4z" clipRule="evenodd" />
            </svg>
          </div>
          <div>
            <p className="font-medium text-white text-sm">Add Site</p>
          </div>
        </div>
      </button>
      
      <button 
        onClick={() => setShowAddTaskModal(true)}
        className="p-3 text-left rounded-lg bg-slate-700/50 hover:bg-green-500/20 transition-colors duration-200 border border-slate-600 hover:border-green-500/50 group"
      >
        <div className="flex items-center">
          <div className="w-8 h-8 bg-green-500/20 rounded-lg flex items-center justify-center mr-3 group-hover:bg-green-500/30 transition-colors duration-200 border border-green-500/30">
            <svg className="w-4 h-4 text-green-400" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M6 2a1 1 0 00-1 1v1H4a2 2 0 00-2 2v10a2 2 0 002 2h12a2 2 0 002-2V6a2 2 0 00-2-2h-1V3a1 1 0 10-2 0v1H7V3a1 1 0 00-1-1zm0 5a1 1 0 000 2h8a1 1 0 100-2H6z" clipRule="evenodd" />
            </svg>
          </div>
          <div>
            <p className="font-medium text-white text-sm">New Task</p>
          </div>
        </div>
      </button>
      
      <button 
        onClick={onNavigateToReports}
        className="p-3 text-left rounded-lg bg-slate-700/50 hover:bg-blue-500/20 transition-colors duration-200 border border-slate-600 hover:border-blue-500/50 group"
      >
        <div className="flex items-center">
          <div className="w-8 h-8 bg-blue-500/20 rounded-lg flex items-center justify-center mr-3 group-hover:bg-blue-500/30 transition-colors duration-200 border border-blue-500/30">
            <svg className="w-4 h-4 text-blue-400" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M3 4a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zm0 4a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zm0 4a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zm0 4a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1z" clipRule="evenodd" />
            </svg>
          </div>
          <div>
            <p className="font-medium text-white text-sm">Create Progress Report</p>
          </div>
        </div>
      </button>
    </div>
  </div>
);

// Subscription Status Widget - Shows flash upgrades, pending downgrades, grace periods, incomplete upgrades
const SubscriptionStatusWidget = ({ 
  hasFlashUpgrade,
  flashUpgrade,
  hasPendingDowngrade,
  pendingDowngrade,
  showGraceBanner,
  daysRemaining,
  totalDays,
  subscriptionTier,
  isGracePeriod,
  hasMidSubscriptionUpgrade,
  midSubscriptionUpgrade,
  navigate
}) => {
  // Only show if there's something to display
  const hasStatus = hasFlashUpgrade || hasPendingDowngrade || showGraceBanner || hasMidSubscriptionUpgrade;
  
  if (!hasStatus) {
    // Show a clean "All Good" status when nothing special
    return (
      <div className="bg-slate-800 rounded-2xl shadow-lg border border-slate-700 p-6 h-full" data-testid="subscription-status-widget">
        <h3 className="text-lg font-semibold text-white mb-4 flex items-center">
          <svg className="w-5 h-5 mr-2 text-purple-400" fill="currentColor" viewBox="0 0 20 20">
            <path fillRule="evenodd" d="M4 4a2 2 0 012-2h8a2 2 0 012 2v12a1 1 0 110 2H6a1 1 0 01-1-1v-3.586l-1.707 1.707a1 1 0 01-1.414-1.414l3-3A1 1 0 016 11h8a1 1 0 010 2H6v3h8V4H6v3h8a1 1 0 010 2H6v3.586l1.707-1.707a1 1 0 011.414 1.414l-3 3A1 1 0 014 16V4z" clipRule="evenodd" />
          </svg>
          Subscription Status
        </h3>
        <div className="flex items-center justify-between p-4 bg-green-500/10 rounded-lg border border-green-500/30">
          <div className="flex items-center">
            <div className="w-10 h-10 bg-green-500/20 rounded-full flex items-center justify-center mr-3">
              <svg className="w-5 h-5 text-green-400" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
              </svg>
            </div>
            <div>
              <p className="font-medium text-green-300">{subscriptionTier || 'Active'}</p>
              <p className="text-xs text-green-200/70">Your subscription is active</p>
            </div>
          </div>
          <button
            onClick={() => navigate('/admin/plans')}
            className="text-xs px-3 py-1.5 bg-slate-700 hover:bg-slate-600 rounded-lg text-gray-300 transition-colors border border-slate-600"
          >
            View Plans
          </button>
        </div>
      </div>
    );
  }
  
  return (
    <div className="bg-slate-800 rounded-2xl shadow-lg border border-slate-700 p-6 h-full" data-testid="subscription-status-widget">
      <h3 className="text-lg font-semibold text-white mb-4 flex items-center">
        <svg className="w-5 h-5 mr-2 text-purple-400" fill="currentColor" viewBox="0 0 20 20">
          <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
        </svg>
        Subscription Status
      </h3>
      
      <div className="space-y-3">
        {/* Flash Upgrade Status */}
        {hasFlashUpgrade && (
          <div className="p-3 bg-yellow-500/10 rounded-lg border border-yellow-500/30" data-testid="flash-upgrade-status">
            <div className="flex items-start justify-between">
              <div className="flex items-start">
                <div className="w-8 h-8 bg-yellow-500/20 rounded-lg flex items-center justify-center mr-3 flex-shrink-0">
                  <svg className="w-4 h-4 text-yellow-400" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M11.3 1.046A1 1 0 0112 2v5h4a1 1 0 01.82 1.573l-7 10A1 1 0 018 18v-5H4a1 1 0 01-.82-1.573l7-10a1 1 0 011.12-.38z" clipRule="evenodd" />
                  </svg>
                </div>
                <div>
                  <div className="flex items-center gap-2">
                    <p className="font-medium text-yellow-300 text-sm">Flash Upgrade Active</p>
                    <span className="px-1.5 py-0.5 bg-yellow-500/20 text-yellow-400 text-xs rounded">Pro Trial</span>
                  </div>
                  <p className="text-xs text-yellow-200/70 mt-1">
                    {flashUpgrade?.days_remaining || 0} days remaining • Expires {flashUpgrade?.expires_at_display || 'soon'}
                  </p>
                </div>
              </div>
            </div>
          </div>
        )}
        
        {/* Pending Downgrade Status */}
        {hasPendingDowngrade && (
          <div className="p-3 bg-amber-500/10 rounded-lg border border-amber-500/30" data-testid="pending-downgrade-status">
            <div className="flex items-start">
              <div className="w-8 h-8 bg-amber-500/20 rounded-lg flex items-center justify-center mr-3 flex-shrink-0">
                <svg className="w-4 h-4 text-amber-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <div>
                <p className="font-medium text-amber-300 text-sm">Downgrade Scheduled</p>
                <p className="text-xs text-amber-200/70 mt-1">
                  {pendingDowngrade?.from_plan} → {pendingDowngrade?.to_plan} on {pendingDowngrade?.effective_date_display || 'next billing'}
                </p>
              </div>
            </div>
          </div>
        )}
        
        {/* Grace Period Status */}
        {showGraceBanner && isGracePeriod && (
          <div className="p-3 bg-blue-500/10 rounded-lg border border-blue-500/30" data-testid="grace-period-status">
            <div className="flex items-start">
              <div className="w-8 h-8 bg-blue-500/20 rounded-lg flex items-center justify-center mr-3 flex-shrink-0">
                <svg className="w-4 h-4 text-blue-400" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-12a1 1 0 10-2 0v4a1 1 0 00.293.707l2.828 2.829a1 1 0 101.415-1.415L11 9.586V6z" clipRule="evenodd" />
                </svg>
              </div>
              <div className="flex-1">
                <p className="font-medium text-blue-300 text-sm">Grace Period</p>
                <p className="text-xs text-blue-200/70 mt-1">
                  {daysRemaining} of {totalDays} days remaining to set up billing
                </p>
                <div className="w-full bg-slate-700 rounded-full h-1.5 mt-2">
                  <div 
                    className="bg-blue-500 h-1.5 rounded-full transition-all duration-500" 
                    style={{ width: `${Math.max(0, (daysRemaining / totalDays) * 100)}%` }}
                  />
                </div>
              </div>
            </div>
          </div>
        )}
        
        {/* Incomplete Upgrade Status */}
        {hasMidSubscriptionUpgrade && (
          <div className="p-3 bg-cyan-500/10 rounded-lg border border-cyan-500/30" data-testid="incomplete-upgrade-status">
            <div className="flex items-start justify-between">
              <div className="flex items-start">
                <div className="w-8 h-8 bg-cyan-500/20 rounded-lg flex items-center justify-center mr-3 flex-shrink-0">
                  <svg className="w-4 h-4 text-cyan-400" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-12a1 1 0 10-2 0v4a1 1 0 00.293.707l2.828 2.829a1 1 0 101.415-1.415L11 9.586V6z" clipRule="evenodd" />
                  </svg>
                </div>
                <div>
                  <div className="flex items-center gap-2">
                    <p className="font-medium text-cyan-300 text-sm">Incomplete Upgrade</p>
                    <span className="px-1.5 py-0.5 bg-cyan-500/20 text-cyan-400 text-xs rounded">Action Required</span>
                  </div>
                  <p className="text-xs text-cyan-200/70 mt-1">
                    £{midSubscriptionUpgrade?.amount_paid?.toFixed(2) || '0.00'} paid for {midSubscriptionUpgrade?.target_plan_name || 'upgrade'}
                    {midSubscriptionUpgrade?.hours_remaining != null && (
                      <> • {Math.round(midSubscriptionUpgrade.hours_remaining)}h remaining</>
                    )}
                  </p>
                </div>
              </div>
            </div>
          </div>
        )}
        
        {/* CTA Button */}
        <button
          onClick={() => navigate('/admin/plans')}
          className="w-full mt-2 px-4 py-2 bg-gradient-to-r from-purple-600 to-indigo-600 hover:from-purple-500 hover:to-indigo-500 text-white text-sm font-medium rounded-lg transition-all"
        >
          {hasMidSubscriptionUpgrade ? 'Complete Upgrade' : hasFlashUpgrade ? 'Upgrade to Pro' : hasPendingDowngrade ? 'Review Plans' : 'Complete Setup'}
        </button>
      </div>
    </div>
  );
};

const DashboardOverviewEnhanced = ({ users, sites, fetchUsers, fetchSites, fetchTasks }) => {
  const navigate = useNavigate();
  const { subscriptionFeatures, loading: featuresLoading } = useSubscriptionFeatures();
  const { 
    hasFlashUpgrade, 
    flashUpgrade, 
    hasPendingDowngrade, 
    pendingDowngrade, 
    showGraceBanner, 
    daysRemaining, 
    totalDays, 
    subscriptionTier,
    isGracePeriod,
    hasMidSubscriptionUpgrade,
    midSubscriptionUpgrade
  } = useSubscription();
  const [currentTime, setCurrentTime] = useState(new Date());
  const [showAddSiteModal, setShowAddSiteModal] = useState(false);
  const [showAddTaskModal, setShowAddTaskModal] = useState(false);
  const [showPushNotificationNotice, setShowPushNotificationNotice] = useState(true);
  const [showWelcomeIntro, setShowWelcomeIntro] = useState(false);
  const [organisationName, setOrganisationName] = useState('');
  
  // Dashboard data states
  const [photosCount, setPhotosCount] = useState(0);
  const [tasksCount, setTasksCount] = useState(0);
  const [loading, setLoading] = useState(true);
  
  // New widget data states
  const [attendanceData, setAttendanceData] = useState([]);  // Array of site attendance data
  const [completionData, setCompletionData] = useState([]);
  const [weatherData, setWeatherData] = useState([]);
  const [recentActivity, setRecentActivity] = useState([]);
  const [teamSettings, setTeamSettings] = useState({ site_start_time: '08:00' }); // Default fallback
  const [storageData, setStorageData] = useState(null);
  const [storageLoading, setStorageLoading] = useState(true);

  // Widget ordering state (header is now separate and fixed)
  const [widgets, setWidgets] = useState(() => {
    try {
      const saved = localStorage.getItem('dashboardWidgetOrder');
      console.log('Loading widget order from localStorage:', saved);
      if (saved) {
        const parsed = JSON.parse(saved);
        // Ensure subscriptionStatus widget is included if not present
        if (!parsed.includes('subscriptionStatus')) {
          parsed.splice(1, 0, 'subscriptionStatus'); // Add after metrics
        }
        return parsed;
      }
      return [
        'metrics',            // Row 1, Col 1: Key Metrics
        'subscriptionStatus', // Row 1, Col 2: Subscription Status (NEW)
        'quickActions',       // Row 2, Col 1: Quick Actions  
        'weather',            // Row 2, Col 2: Weather Info
        'recentActivity',     // Row 3, Col 1: Recent Activity
        'attendance',         // Row 3, Col 2: Clock-In Status
        'taskCompletion'      // Row 4: Tasks
      ];
    } catch (error) {
      console.error('Failed to load widget order from localStorage:', error);
      return [
        'metrics',            // Row 1, Col 1: Key Metrics
        'subscriptionStatus', // Row 1, Col 2: Subscription Status (NEW)
        'quickActions',       // Row 2, Col 1: Quick Actions  
        'weather',            // Row 2, Col 2: Weather Info
        'recentActivity',     // Row 3, Col 1: Recent Activity
        'attendance',         // Row 3, Col 2: Clock-In Status
        'taskCompletion'      // Row 4: Tasks
      ];
    }
  });

  // Save widget order to localStorage whenever it changes
  useEffect(() => {
    try {
      localStorage.setItem('dashboardWidgetOrder', JSON.stringify(widgets));
      console.log('Widget order persisted:', widgets);
    } catch (error) {
      console.error('Failed to persist widget order:', error);
    }
  }, [widgets]);

  // Drag and drop sensors
  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  useEffect(() => {
    const timer = setInterval(() => setCurrentTime(new Date()), 60000);
    fetchDashboardData();
    checkWelcomeIntro();
    fetchOrganisationName();
    return () => clearInterval(timer);
  }, []);

  // Fetch organisation name for display
  const fetchOrganisationName = async () => {
    try {
      const response = await axios.get(`${API}/admin/organisation`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });
      setOrganisationName(response.data?.name || '');
    } catch (error) {
      console.error('Error fetching organisation name:', error);
    }
  };

  // Check if admin has seen the welcome intro
  const checkWelcomeIntro = async () => {
    try {
      const response = await axios.get(`${API}/admin/preferences`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });
      const preferences = response.data?.data || response.data || {};
      
      // Show welcome intro only if:
      // - has_completed_guided_tour is true (tour completed or skipped)
      // - has_seen_welcome_intro is false (hasn't seen welcome modal yet)
      const guidedTourCompleted = preferences.has_completed_guided_tour === true;
      const welcomeIntroSeen = preferences.has_seen_welcome_intro === true;
      
      if (guidedTourCompleted && !welcomeIntroSeen) {
        // Also try to get the organisation name for the modal
        try {
          const orgResponse = await axios.get(`${API}/admin/organisation`, {
            headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
          });
          setOrganisationName(orgResponse.data?.name || '');
        } catch (orgError) {
          console.log('Could not fetch organisation name');
        }
        setShowWelcomeIntro(true);
      }
    } catch (error) {
      console.error('Error checking welcome intro status:', error);
      // Don't show the modal if we can't fetch preferences
    }
  };

  // Function to show welcome modal (called after guided tour completes)
  const showWelcomeModal = async () => {
    try {
      const orgResponse = await axios.get(`${API}/admin/organisation`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });
      setOrganisationName(orgResponse.data?.name || '');
    } catch (orgError) {
      console.log('Could not fetch organisation name');
    }
    setShowWelcomeIntro(true);
  };

  // Listen for guided tour completion event
  useEffect(() => {
    const handleGuidedTourCompleted = () => {
      showWelcomeModal();
    };

    window.addEventListener('guidedTourCompleted', handleGuidedTourCompleted);
    return () => {
      window.removeEventListener('guidedTourCompleted', handleGuidedTourCompleted);
    };
  }, []);

  // Refetch attendance data when sites, users, or team settings change
  useEffect(() => {
    if (sites.length > 0 && users.length > 0 && teamSettings.site_start_time) {
      fetchAttendanceData();
    }
  }, [sites, users, teamSettings]);

  // Refetch weather data when sites change
  useEffect(() => {
    if (sites.length > 0) {
      fetchWeatherData();
    }
  }, [sites]);

  // Helper functions for week calculations
  const getWeekStart = (date) => {
    const d = new Date(date);
    const day = d.getDay();
    const diff = d.getDate() - day + (day === 0 ? -6 : 1); // Monday as week start
    const monday = new Date(d.setDate(diff));
    monday.setHours(0, 0, 0, 0);
    return monday;
  };

  const getWeekEnd = (date) => {
    const weekStart = getWeekStart(date);
    const sunday = new Date(weekStart);
    sunday.setDate(weekStart.getDate() + 6); // Sunday (Monday + 6 days)
    sunday.setHours(23, 59, 59, 999);
    return sunday;
  };

  const fetchDashboardData = async () => {
    try {
      // Fetch basic data first, including team settings
      await Promise.all([
        fetchPhotosCount(),
        fetchTasksCount(),
        fetchTaskCompletion(),
        fetchRecentActivity(),
        fetchWeatherData(),
        fetchTeamSettings(),
        fetchStorageUsage()
      ]);
      
      // Fetch attendance data after team settings are available
      await fetchAttendanceData();
    } catch (error) {
      console.error('Error fetching dashboard data:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchPhotosCount = async () => {
    try {
      // Use dedicated endpoint for photos this week (more efficient)
      const response = await axios.get(`${API}/admin/dashboard/photos-this-week`);
      setPhotosCount(response.data?.count || 0);
    } catch (error) {
      // Fallback to client-side filtering if endpoint not available
      try {
        const response = await axios.get(`${API}/admin/photos`);
        const photos = response.data || [];
        
        // Filter photos for current week (Monday to Saturday)
        const now = new Date();
        const currentWeekStart = getWeekStart(now);
        const currentWeekEnd = getWeekEnd(now);
        
        const weekPhotos = photos.filter(photo => {
          if (!photo.created_at) return false;
          const photoDate = new Date(photo.created_at);
          return photoDate >= currentWeekStart && photoDate <= currentWeekEnd;
        });
        
        setPhotosCount(weekPhotos.length);
      } catch (fallbackError) {
        setPhotosCount(0);
      }
    }
  };

  const fetchTasksCount = async () => {
    try {
      const response = await axios.get(`${API}/admin/tasks`);
      const tasks = response.data || [];
      
      // Calculate current week boundaries (Monday to Sunday)
      const now = new Date();
      const currentWeekStart = getWeekStart(now);
      const currentWeekEnd = new Date(currentWeekStart);
      currentWeekEnd.setDate(currentWeekEnd.getDate() + 6); // Sunday
      currentWeekEnd.setHours(23, 59, 59, 999);
      
      // Filter for active tasks (pending or in_progress) with due date in current week
      const activeWeekTasks = tasks.filter(task => {
        // Task must be pending or in_progress
        const isActive = task.status === 'pending' || task.status === 'in_progress';
        if (!isActive) return false;
        
        // Task must have a due date within current week
        if (!task.due_date) return false;
        
        const dueDate = new Date(task.due_date);
        return dueDate >= currentWeekStart && dueDate <= currentWeekEnd;
      });
      
      setTasksCount(activeWeekTasks.length);
    } catch (error) {
      setTasksCount(0);
    }
  };

  // Real data fetching functions
  const fetchAttendanceData = async () => {
    try {
      // Get current date for today's attendance
      const today = new Date();
      const todayStr = today.toISOString().split('T')[0]; // YYYY-MM-DD format
      
      // Get active sites
      const activeSites = sites.filter(site => site.status === 'active');
      
      if (activeSites.length === 0) {
        setAttendanceData([]);
        return;
      }
      
      // Fetch attendance data for each active site
      const siteAttendancePromises = activeSites.map(async (site) => {
        try {
          // Fetch attendance data for this specific site
          const response = await axios.get(`${API}/admin/attendance?start_date=${todayStr}&end_date=${todayStr}&site_id=${site.id}`, {
            headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
          });
          
          const attendanceRecords = response.data || [];
          
          // Filter for currently clocked in users and deduplicate by user_id
          // (prevents double counting if user has multiple sessions on the same day)
          const clockedInRecords = attendanceRecords.filter(record => record.is_currently_logged_in);
          const uniqueClockedInRecords = [];
          const seenUserIds = new Set();
          
          for (const record of clockedInRecords) {
            const userId = record.user_id || record.id;
            if (!seenUserIds.has(userId)) {
              seenUserIds.add(userId);
              uniqueClockedInRecords.push(record);
            }
          }
          
          // Calculate late arrivals (users who logged in after site opening time)
          const siteStartTime = teamSettings.site_start_time || '08:00';
          const [startHour, startMinute] = siteStartTime.split(':').map(Number);
          
          const lateRecords = uniqueClockedInRecords.filter(record => {
            if (!record.login_timestamp) return false;
            const loginTime = new Date(record.login_timestamp);
            const loginHour = loginTime.getHours();
            const loginMinutes = loginTime.getMinutes();
            const loginTotalMinutes = loginHour * 60 + loginMinutes;
            const openingTotalMinutes = startHour * 60 + startMinute;
            return loginTotalMinutes > openingTotalMinutes;
          });
          
          // Prepare worker lists for tooltips
          const clockedInWorkers = uniqueClockedInRecords.map(record => ({
            name: record.full_name || record.username,
            clockInTime: new Date(record.login_timestamp).toLocaleTimeString('en-GB', { 
              hour: '2-digit', 
              minute: '2-digit' 
            })
          }));
          
          const lateWorkers = lateRecords.map(record => ({
            name: record.full_name || record.username,
            lateDuration: calculateLateDuration(record.login_timestamp, siteStartTime),
            clockInTime: new Date(record.login_timestamp).toLocaleTimeString('en-GB', { 
              hour: '2-digit', 
              minute: '2-digit' 
            })
          }));
          
          return {
            site_id: site.id,  // Use internal ID for consistency
            site_name: site.site_name,
            clockedIn: uniqueClockedInRecords.length,
            late: lateRecords.length,
            clockedInWorkers: clockedInWorkers,
            lateWorkers: lateWorkers
          };
        } catch (error) {
          console.error(`Error fetching attendance for site ${site.site_id}:`, error);
          return {
            site_id: site.id,  // Use internal ID for consistency
            site_name: site.site_name,
            clockedIn: 0,
            late: 0,
            clockedInWorkers: [],
            lateWorkers: []
          };
        }
      });
      
      const siteAttendanceData = await Promise.all(siteAttendancePromises);
      setAttendanceData(siteAttendanceData);
      
    } catch (error) {
      console.error('Error fetching attendance data:', error);
      setAttendanceData([]);
    }
  };

  // Helper function to calculate how late someone was (using dynamic opening time)
  const calculateLateDuration = (loginTimestamp, siteStartTime = '08:00') => {
    const loginTime = new Date(loginTimestamp);
    const expectedTime = new Date(loginTime);
    
    // Parse site start time (format: "HH:MM")
    const [startHour, startMinute] = siteStartTime.split(':').map(Number);
    expectedTime.setHours(startHour, startMinute, 0, 0);
    
    if (loginTime <= expectedTime) return '0 min';
    
    const diffMs = loginTime - expectedTime;
    const diffMinutes = Math.floor(diffMs / (1000 * 60));
    
    if (diffMinutes < 60) {
      return `${diffMinutes} min`;
    } else {
      const hours = Math.floor(diffMinutes / 60);
      const minutes = diffMinutes % 60;
      return minutes > 0 ? `${hours}h ${minutes}m` : `${hours}h`;
    }
  };

  const fetchTaskCompletion = async () => {
    try {
      // Fetch tasks and calculate completion metrics for current week per site
      const response = await axios.get(`${API}/admin/tasks`);
      const tasks = response.data || [];
      
      // Get current week boundaries (Monday to Sunday)
      const now = new Date();
      const currentWeekStart = getWeekStart(now);
      const currentWeekEnd = getWeekEnd(now);
      
      // Filter tasks for current week based on due date (not creation date)
      const weekTasks = tasks.filter(task => {
        if (!task.due_date) return false;
        const dueDate = new Date(task.due_date);
        return dueDate >= currentWeekStart && dueDate <= currentWeekEnd;
      });
      
      // Get active sites only
      const activeSites = sites.filter(site => site.status === 'active');
      
      // Calculate completion metrics per site
      const siteTaskData = activeSites.map(site => {
        const siteTasks = weekTasks.filter(task => task.site_id === site.id);
        
        const total = siteTasks.length;
        const completed = siteTasks.filter(task => task.status === 'completed').length;
        const pending = siteTasks.filter(task => 
          task.status !== 'completed' && task.status !== 'cancelled'
        ).length;
        
        return {
          site_id: site.id,
          site_name: site.site_name,
          total,
          completed,
          pending
        };
      });
      
      setCompletionData(siteTaskData);
    } catch (error) {
      console.error('Error fetching task completion data:', error);
      setCompletionData([]);
    }
  };

  const fetchWeatherData = async () => {
    try {
      // Get ALL active sites (both with and without addresses)
      const activeSites = sites.filter(site => site.status === 'active');

      if (activeSites.length === 0) {
        setWeatherData([]);
        return;
      }

      // Helper to build full address from structured fields
      const getFullAddress = (site) => {
        const parts = [
          site.address_line_1,
          site.address_line_2,
          site.city,
          site.postcode,
          site.country
        ].filter(p => p && p.trim());
        return parts.join(', ');
      };

      // Process each site to get weather data
      const siteWeatherPromises = activeSites.map(async (site) => {
        const fullAddress = getFullAddress(site);
        const hasAddress = fullAddress.length > 0 && site.address_line_1 && site.city;
        
        if (hasAddress) {
          try {
            // Make API call to get real weather data
            const response = await axios.get(`${API}/admin/weather`, {
              params: { address: fullAddress },
              headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
            });
            
            return {
              site_id: site.id,
              site_name: site.site_name,
              temperature: response.data.temperature,
              condition: response.data.condition,
              icon: response.data.icon,
              alerts: response.data.alerts || [],
              lastUpdated: formatTime(currentTime),
              hasAddress: true,
              location: response.data.location || fullAddress
            };
          } catch (error) {
            console.error(`Error fetching weather for ${site.site_name}:`, error);
            // Fall back to error state for this site
            return {
              site_id: site.id,
              site_name: site.site_name,
              temperature: null,
              condition: 'Error',
              icon: '❌',
              alerts: [{ type: 'Error', message: 'Unable to fetch weather data' }],
              lastUpdated: '',
              hasAddress: true,
              location: fullAddress
            };
          }
        } else {
          // Sites without addresses get a prompt
          return {
            site_id: site.id,
            site_name: site.site_name,
            temperature: null,
            condition: null,
            icon: '🏗️',
            alerts: [],
            lastUpdated: '',
            hasAddress: false,
            location: ''
          };
        }
      });

      // Wait for all weather data to be fetched
      const siteWeatherData = await Promise.all(siteWeatherPromises);
      setWeatherData(siteWeatherData);
    } catch (error) {
      console.error('Error fetching weather data:', error);
      setWeatherData([]);
    }
  };

  const fetchRecentActivity = async () => {
    try {
      const response = await axios.get(`${API}/admin/dashboard/activity`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });
      
      const activityData = response.data || {};
      const allActivities = activityData.activities || [];
      
      // Filter activities to only show data from the previous 3 days
      const threeDaysAgo = new Date();
      threeDaysAgo.setDate(threeDaysAgo.getDate() - 3);
      threeDaysAgo.setHours(0, 0, 0, 0); // Start of the day 3 days ago
      
      const filteredActivities = allActivities.filter(activity => {
        if (!activity.timestamp) return false;
        const activityDate = new Date(activity.timestamp);
        return activityDate >= threeDaysAgo;
      });
      
      setRecentActivity(filteredActivities);
    } catch (error) {
      console.error('Error fetching recent activity:', error);
      setRecentActivity([]);
    }
  };

  const fetchTeamSettings = async () => {
    try {
      const response = await axios.get(`${API}/admin/organisation-settings`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });
      
      const settings = response.data || {};
      // Map organisation-settings fields to match expected format
      const mappedSettings = {
        ...settings,
        site_start_time: settings.start_time || '08:00', // Map for backward compatibility
        site_end_time: settings.end_time || '17:00',
        site_working_days: settings.working_days || ['monday', 'tuesday', 'wednesday', 'thursday', 'friday']
      };
      setTeamSettings(mappedSettings);
    } catch (error) {
      console.error('Error fetching team settings:', error);
      // Keep default fallback values
      setTeamSettings({ site_start_time: '08:00' });
    }
  };

  const fetchStorageUsage = async () => {
    try {
      setStorageLoading(true);
      const response = await axios.get(`${API}/admin/dashboard/storage-usage`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });
      if (response.data?.success) {
        setStorageData(response.data);
      }
    } catch (error) {
      console.error('Error fetching storage usage:', error);
      setStorageData(null);
    } finally {
      setStorageLoading(false);
    }
  };

  const formatTime = (date) => {
    return date.toLocaleTimeString('en-GB', { 
      hour: '2-digit', 
      minute: '2-digit',
      hour12: false 
    });
  };

  const formatDate = (date) => {
    return date.toLocaleDateString('en-GB', { 
      weekday: 'long',
      year: 'numeric', 
      month: 'long', 
      day: 'numeric' 
    });
  };

  const handleDragEnd = (event) => {
    const { active, over } = event;

    // Ensure we have both active and over elements
    if (!active || !over) {
      console.log('Drag ended without valid drop target');
      return;
    }

    // Only reorder if the position actually changed
    if (active.id !== over.id) {
      setWidgets((items) => {
        const oldIndex = items.indexOf(active.id);
        const newIndex = items.indexOf(over.id);
        
        // Validate indices
        if (oldIndex === -1 || newIndex === -1) {
          console.error('Invalid widget indices during drag:', { oldIndex, newIndex, activeId: active.id, overId: over.id });
          return items;
        }
        
        const newOrder = arrayMove(items, oldIndex, newIndex);
        
        // Save to localStorage immediately
        try {
          localStorage.setItem('dashboardWidgetOrder', JSON.stringify(newOrder));
          console.log('Widget order saved to localStorage:', newOrder);
        } catch (error) {
          console.error('Failed to save widget order to localStorage:', error);
        }
        
        return newOrder;
      });
    }
  };

  const renderWidget = (widgetId) => {
    switch (widgetId) {
      case 'metrics':
        return <MetricsCardsWidget users={users} sites={sites} photosCount={photosCount} tasksCount={tasksCount} loading={loading} />;
      case 'attendance':
        return <TodaysAttendanceWidget attendanceData={attendanceData} loading={loading} />;
      case 'taskCompletion':
        return <TaskCompletionWidget completionData={completionData} loading={loading} />;
      case 'weather':
        return <WeatherImpactWidget 
          weatherData={weatherData} 
          loading={loading} 
        />;
      case 'recentActivity':
        return <RecentActivityWidget 
          recentActivity={recentActivity} 
          loading={loading} 
        />;
      case 'quickActions':
        return <QuickActionsWidget 
          navigate={navigate}
          setShowAddSiteModal={setShowAddSiteModal}
          setShowAddTaskModal={setShowAddTaskModal}
          onNavigateToReports={() => navigate('/admin/reports/progress')}
        />;
      case 'subscriptionStatus':
        return <SubscriptionStatusWidget 
          hasFlashUpgrade={hasFlashUpgrade}
          flashUpgrade={flashUpgrade}
          hasPendingDowngrade={hasPendingDowngrade}
          pendingDowngrade={pendingDowngrade}
          showGraceBanner={showGraceBanner}
          daysRemaining={daysRemaining}
          totalDays={totalDays}
          subscriptionTier={subscriptionTier}
          isGracePeriod={isGracePeriod}
          hasMidSubscriptionUpgrade={hasMidSubscriptionUpgrade}
          midSubscriptionUpgrade={midSubscriptionUpgrade}
          navigate={navigate}
        />;
      default:
        return null;
    }
  };

  return (
    <div className="space-y-6 sm:space-y-8">
      {/* Fixed Header - Not draggable */}
      <div className="bg-gradient-to-r from-purple-600 via-purple-700 to-indigo-700 rounded-2xl p-6 sm:p-8 border border-purple-500/30 shadow-lg">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
          <div className="mb-4 sm:mb-0">
            <div className="flex items-center gap-3 mb-2 flex-wrap">
              <h1 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-white">
                Welcome to SiteOrbit
              </h1>
              {organisationName && (
                <span 
                  className="inline-flex bg-purple-500 text-white text-xs font-semibold px-3 py-1.5 rounded-full whitespace-nowrap"
                  data-testid="dashboard-org-badge"
                >
                  {organisationName}
                </span>
              )}
            </div>
            <p className="text-white/80 text-sm sm:text-base">
              Advanced construction site management dashboard
            </p>
          </div>
          <div className="text-right">
            <div className="text-2xl sm:text-3xl font-bold text-white">
              {formatTime(currentTime)}
            </div>
            <div className="text-xs sm:text-sm text-white/70 mt-1">
              {formatDate(currentTime)}
            </div>
          </div>
        </div>
      </div>

      {/* Push Notifications Disabled Notice */}
      {subscriptionFeatures && !subscriptionFeatures.pushNotifications && showPushNotificationNotice && (
        <SubscriptionFeatureNotice 
          featureName="Push Notifications" 
          onClose={() => setShowPushNotificationNotice(false)}
        />
      )}

      {/* Layout Controls - Below Header */}
      <div className="flex items-center justify-between mb-4 px-1">
        <div>
          <h2 className="text-sm font-medium text-gray-300">Dashboard Layout</h2>
          <p className="text-xs text-gray-500">Drag widgets to reorder them</p>
        </div>
        <button
          onClick={() => {
            const defaultOrder = ['metrics', 'subscriptionStatus', 'quickActions', 'weather', 'recentActivity', 'attendance', 'taskCompletion'];
            try {
              localStorage.removeItem('dashboardWidgetOrder');
              setWidgets(defaultOrder);
              console.log('Widget order reset to default:', defaultOrder);
            } catch (error) {
              console.error('Failed to reset widget order:', error);
            }
          }}
          className="text-xs px-3 py-1 bg-slate-700 hover:bg-slate-600 rounded-md text-gray-300 transition-colors border border-slate-600"
        >
          Reset Layout
        </button>
      </div>

      {/* Draggable Widgets in Grid Layout */}
      <DndContext 
        sensors={sensors}
        collisionDetection={closestCenter}
        onDragEnd={handleDragEnd}
      >
        <SortableContext items={widgets} strategy={rectSortingStrategy}>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 overflow-visible">
            {widgets.map((widgetId) => (
              <SortableWidget key={widgetId} id={widgetId}>
                {renderWidget(widgetId)}
              </SortableWidget>
            ))}
          </div>
        </SortableContext>
      </DndContext>

      {/* Storage Usage Section - Fixed, not draggable */}
      <div className="bg-slate-800 rounded-2xl shadow-lg border border-slate-700 p-6" data-testid="storage-usage-section">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center">
            <div className="w-10 h-10 bg-gradient-to-br from-slate-600 to-slate-700 rounded-xl flex items-center justify-center mr-3">
              <svg className="w-5 h-5 text-slate-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 7v10c0 2.21 3.582 4 8 4s8-1.79 8-4V7M4 7c0 2.21 3.582 4 8 4s8-1.79 8-4M4 7c0-2.21 3.582-4 8-4s8 1.79 8 4m0 5c0 2.21-3.582 4-8 4s-8-1.79-8-4" />
              </svg>
            </div>
            <div>
              <h3 className="text-lg font-semibold text-white">Storage Usage</h3>
              <p className="text-xs text-gray-400">Organisation file storage</p>
            </div>
          </div>
          <button 
            onClick={fetchStorageUsage}
            className="p-2 text-gray-400 hover:text-white hover:bg-slate-700 rounded-lg transition-colors"
            title="Refresh storage data"
          >
            <svg className={`w-5 h-5 ${storageLoading ? 'animate-spin' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
            </svg>
          </button>
        </div>
        
        {storageLoading && !storageData ? (
          <div className="flex items-center justify-center py-8">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-500"></div>
          </div>
        ) : storageData?.storage ? (
          <div className="space-y-4">
            {/* Progress Bar */}
            <div>
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm font-medium text-gray-300">
                  {storageData.storage.used_display} {storageData.storage.used_unit} used
                  {storageData.storage.status !== 'unlimited' && (
                    <span className="text-gray-500"> of {storageData.storage.limit_gb} GB</span>
                  )}
                </span>
                <span className={`text-sm font-semibold ${
                  storageData.storage.status === 'critical' ? 'text-red-400' :
                  storageData.storage.status === 'warning' ? 'text-amber-400' :
                  storageData.storage.status === 'unlimited' ? 'text-gray-400' :
                  'text-green-400'
                }`}>
                  {storageData.storage.status === 'unlimited' ? 'Unlimited' : `${storageData.storage.usage_percentage}%`}
                </span>
              </div>
              
              {storageData.storage.status !== 'unlimited' && (
                <div className="w-full bg-slate-700 rounded-full h-3 overflow-hidden">
                  <div
                    className={`h-full rounded-full transition-all duration-500 ${
                      storageData.storage.status === 'critical' ? 'bg-red-500' :
                      storageData.storage.status === 'warning' ? 'bg-amber-500' :
                      'bg-green-500'
                    }`}
                    style={{ width: `${Math.min(storageData.storage.usage_percentage, 100)}%` }}
                  />
                </div>
              )}
            </div>
            
            {/* Subscription Tier Info */}
            <div className="flex items-center justify-between text-sm">
              <span className="text-gray-400">
                Subscription: <span className="font-medium text-gray-300">{storageData.storage.tier_name}</span>
              </span>
              <span className="text-xs text-gray-500">
                Last updated: {new Date(storageData.last_updated).toLocaleTimeString('en-GB', { 
                  hour: '2-digit', 
                  minute: '2-digit'
                })}
              </span>
            </div>
            
            {/* Warning Message at 95%+ */}
            {(storageData.storage.status === 'warning' || storageData.storage.status === 'critical') && (
              <div className={`mt-4 p-4 rounded-lg border ${
                storageData.storage.status === 'critical' 
                  ? 'bg-red-500/10 border-red-500/30' 
                  : 'bg-amber-500/10 border-amber-500/30'
              }`}>
                <div className="flex items-start">
                  <svg className={`w-5 h-5 mt-0.5 mr-3 flex-shrink-0 ${
                    storageData.storage.status === 'critical' ? 'text-red-400' : 'text-amber-400'
                  }`} fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                  </svg>
                  <div>
                    <h4 className={`font-semibold ${
                      storageData.storage.status === 'critical' ? 'text-red-300' : 'text-amber-300'
                    }`}>
                      {storageData.storage.status === 'critical' ? 'Storage Almost Full' : 'Storage Warning'}
                    </h4>
                    <p className={`text-sm mt-1 ${
                      storageData.storage.status === 'critical' ? 'text-red-200/80' : 'text-amber-200/80'
                    }`}>
                      Your storage is at {storageData.storage.usage_percentage}% capacity. 
                      {storageData.storage.status === 'critical' 
                        ? ' You will not be able to upload new photos, documents, or reports once storage is full.'
                        : ' Consider freeing up space to avoid disruption.'
                      }
                    </p>
                    <p className={`text-sm mt-2 ${
                      storageData.storage.status === 'critical' ? 'text-red-200/70' : 'text-amber-200/70'
                    }`}>
                      <strong>To resolve:</strong> Delete unused files and photos, or upgrade to a higher subscription tier with more storage allowance.
                    </p>
                  </div>
                </div>
              </div>
            )}
          </div>
        ) : (
          <div className="text-center py-6 text-gray-400">
            <p>Unable to load storage information</p>
            <button 
              onClick={fetchStorageUsage}
              className="mt-2 text-sm text-purple-400 hover:text-purple-300"
            >
              Try again
            </button>
          </div>
        )}
      </div>

      {/* Modals */}
      <AddSiteModal
        isOpen={showAddSiteModal}
        onClose={() => setShowAddSiteModal(false)}
        onSiteCreated={(newSite) => {
          if (fetchSites) {
            fetchSites();
          }
        }}
      />
      
      <AddTaskModal
        isOpen={showAddTaskModal}
        onClose={() => setShowAddTaskModal(false)}
        onTaskCreated={(newTask) => {
          if (fetchTasks) {
            fetchTasks();
          }
        }}
      />

      {/* Welcome Intro Modal - First-time login */}
      <WelcomeIntroModal
        isOpen={showWelcomeIntro}
        onClose={() => setShowWelcomeIntro(false)}
        organisationName={organisationName}
      />
    </div>
  );
};

export default DashboardOverviewEnhanced;